import { motion } from 'framer-motion'
import {
  HiOutlineShieldCheck,
  HiOutlineEye,
  HiOutlineLightningBolt,
  HiOutlineUserGroup,
} from 'react-icons/hi'
import type { IconType } from 'react-icons'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { isRtl, useLocale } from '@/lib/locale'
import { copy } from './Slide14DefenseSystem.copy'

const ease = [0.16, 1, 0.3, 1] as const
const transitionBase = { duration: 0.45, ease }
const animationProps = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } }

type LayerAccent = {
  // Outer card
  border: string
  bg: string
  // Icon tile
  iconTile: string
  Icon: IconType
}

const ACCENTS: readonly LayerAccent[] = [
  {
    border: 'border-violet-100',
    bg: 'bg-violet-50/30',
    iconTile: 'text-violet-600 bg-violet-100',
    Icon: HiOutlineShieldCheck,
  },
  {
    border: 'border-amber-100',
    bg: 'bg-amber-50/30',
    iconTile: 'text-amber-600 bg-amber-100',
    Icon: HiOutlineEye,
  },
  {
    border: 'border-emerald-100',
    bg: 'bg-emerald-50/30',
    iconTile: 'text-emerald-600 bg-emerald-100',
    Icon: HiOutlineLightningBolt,
  },
  {
    border: 'border-sky-100',
    bg: 'bg-sky-50/30',
    iconTile: 'text-sky-600 bg-sky-100',
    Icon: HiOutlineUserGroup,
  },
]

const MOBILE_ICON_TILES = [
  'bg-violet-100 text-violet-600',
  'bg-amber-100 text-amber-600',
  'bg-emerald-100 text-emerald-600',
  'bg-sky-100 text-sky-600',
] as const

export default function Slide14DefenseSystem(_props: SlideProps) {
  const c = useSlideCopy(copy)
  // Desktop cards start stacked on the first card (the inline start) and
  // spread toward the inline end. Framer's `x` is physical, so mirror it.
  const stackDir = isRtl(useLocale()) ? 1 : -1

  const layers = [
    { label: c.layer1Label, title: c.layer1Title, body: c.layer1Body },
    { label: c.layer2Label, title: c.layer2Title, body: c.layer2Body },
    { label: c.layer3Label, title: c.layer3Title, body: c.layer3Body },
    { label: c.layer4Label, title: c.layer4Title, body: c.layer4Body },
  ]

  return (
    <section className="text-slate-900 min-h-[70vh] w-full flex flex-col justify-center">
      <div className="w-full max-w-4xl mx-auto">
        {/* Title + intro */}
        <motion.div
          {...animationProps}
          transition={{ ...transitionBase, delay: 0 }}
          className="mb-5 max-w-xl text-center mx-auto"
        >
          <h2 className="text-5xl sm:text-6xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1] text-center">
            {c.title}
          </h2>
        </motion.div>

        <motion.p
          {...animationProps}
          transition={{ ...transitionBase, delay: 0.1 }}
          className="text-xl sm:text-2xl mb-10 leading-relaxed text-slate-600 max-w-2xl text-center mx-auto"
        >
          {c.body}
        </motion.p>

        {/* Layer cards */}
        <div className="mt-6">
          {/* Desktop: 4-column grid. Cards start stacked on the left and
              spread out into their final positions with a staggered ease. */}
          <div className="relative hidden lg:grid gap-4 lg:grid-cols-4">
            {layers.map((layer, index) => {
              const accent = ACCENTS[index]
              const { Icon } = accent
              return (
                <motion.div
                  key={layer.title}
                  custom={index}
                  initial="stacked"
                  animate="spread"
                  variants={{
                    stacked: (i: number) => ({
                      x: stackDir * i * 220,
                      opacity: Math.max(0.2, 1 - i * 0.25),
                    }),
                    spread: (i: number) => ({
                      x: 0,
                      opacity: 1,
                      transition: {
                        delay: 0.1 + i * 0.08,
                        duration: 0.7,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    }),
                  }}
                  style={{ zIndex: layers.length - index }}
                  className={`rounded-2xl border p-5 shadow-sm ${accent.border} ${accent.bg}`}
                >
                  <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${accent.iconTile}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-slate-900">{layer.title}</h3>
                  <p className="mt-2 text-lg text-slate-600 leading-relaxed">{layer.body}</p>
                </motion.div>
              )
            })}
          </div>

          {/* Tablet & mobile: vertical stack with horizontal cards separated by a hairline */}
          <div className="relative flex flex-col gap-3 lg:hidden">
            {layers.map((layer, index) => {
              const accent = ACCENTS[index]
              const { Icon } = accent
              const isLast = index === layers.length - 1
              return (
                <motion.div
                  key={layer.title}
                  custom={index}
                  initial="stacked"
                  animate="spread"
                  variants={{
                    stacked: (i: number) => ({
                      y: -i * 92,
                      opacity: Math.max(0.2, 1 - i * 0.25),
                    }),
                    spread: (i: number) => ({
                      y: 0,
                      opacity: 1,
                      transition: {
                        delay: 0.1 + i * 0.08,
                        duration: 0.7,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    }),
                  }}
                  className={`px-5 py-4 flex items-center gap-5 ${
                    isLast ? '' : 'border-b border-slate-200'
                  }`}
                >
                  <div
                    className={`h-12 w-12 rounded-2xl flex-shrink-0 flex items-center justify-center ${MOBILE_ICON_TILES[index]}`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-slate-900">{layer.title}</h3>
                    <p className="mt-1 text-lg sm:text-xl text-slate-600 leading-relaxed">
                      {layer.body}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Footer */}
          <motion.p
            {...animationProps}
            transition={{ ...transitionBase, delay: 0.8 }}
            className="mt-6 text-center text-lg text-slate-500 italic"
          >
            {c.footer}
          </motion.p>
        </div>
      </div>
    </section>
  )
}
